import React, { useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Button, InputAdornment, Typography } from '@mui/material';
import IconicHeader from '../shared/IconicHeader';
import InputField from '../shared/TextField';
import GenericButton from '../shared/Button';
import usdxxLogo from '../../../assets/updated/SmartUSDXX/usdxxLogo.png';
import usdxxEarnArt from '../../../assets/updated/SmartUSDXX/usdxxEarnArt.png';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: '1380px',
    margin: '70px auto',
    padding: '20px',
  },
  flexContainerRoot: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    marginBottom: '20px',
    '& img': {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
    },
    '& h2': {
      margin: 0,
      fontSize: '38px',
      fontWeight: '500',
      fontStyle: 'italic',
    },
  },
  eyebrow: {
    fontSize: '12px',
    letterSpacing: '1.5px',
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: '10px',
  },
  heading: {
    fontSize: '46px',
    fontWeight: 500,
    lineHeight: 1.2,
    marginBottom: '20px',
    '& em': {
      fontStyle: 'italic',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '30px',
    },
  },
  text: {
    fontSize: '16px',
    fontWeight: 300,
    color: theme.palette.text.secondary,
    marginBottom: '30px',
    maxWidth: '520px',
    '& strong': {
      color: theme.palette.text.primary,
    },
  },
  container: {
    display: 'flex',
    gap: '50px',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  artWrap: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    '& img': {
      maxWidth: '100%',
      width: '380px',
    },
  },
  content: {
    flex: 1,
    width: '100%',
  },
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '24px',
    padding: '30px',
    width: '100%',
    maxWidth: '480px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '.5px',
    color: theme.palette.text.secondary,
    marginBottom: '10px',
  },
  termGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
    margin: '20px 0px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  termButton: {
    display: 'flex !important',
    flexDirection: 'column',
    alignItems: 'flex-start !important',
    justifyContent: 'flex-start !important',
    gap: '4px',
    padding: '12px !important',
    height: 'auto !important',
    minHeight: '68px !important',
    overflow: 'visible !important',
    textAlign: 'left',
    textTransform: 'none !important',
    whiteSpace: 'normal !important',
    '& b': {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '1.3',
    },
    '& span': {
      fontSize: '11px',
      fontWeight: 400,
      lineHeight: '1.4',
    },
  },
  activeButton: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: 'black !important',
  },
  inactiveButton: {
    border: `1px solid ${theme.palette.divider} !important`,
    background: 'none !important',
    color: `${theme.palette.text.primary} !important`,
  },
  rewardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '20px',
    margin: '20px 0px',
    '& > div': {
      flex: 1,
    },
    '& span': {
      display: 'block',
      fontSize: '12px',
      color: theme.palette.text.secondary,
      marginBottom: '6px',
    },
    '& strong': {
      fontSize: '20px',
      fontWeight: 600,
    },
  },
  disclaimer: {
    fontSize: '11px',
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
    marginTop: '20px',
  },
  dropDownIconContainer: {
    display: 'flex',
    alignItems: 'center',
    '& p': {
      fontSize: '14px',
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  },
}));

const EARN_TERMS = {
  3: 3.0,
  6: 3.15,
  12: 3.25,
};

const SmartUSDXX = () => {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState('');
  const [amount, setAmount] = useState('1000');
  const [months, setMonths] = useState('6');

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleAmountChange = (e) => {
    setAmount(e.target.value.replace(/[^0-9.]/g, ''));
  };

  const amountNumber = Math.max(0, Number.parseFloat(amount) || 0);
  const selectedApy = EARN_TERMS[months] ?? 0;

  const estimatedInterest = useMemo(
    () => amountNumber * (selectedApy / 100) * (Number(months) / 12),
    [amountNumber, selectedApy, months]
  );

  const estimatedTotal = amountNumber + estimatedInterest;

  return (
    <div className={classes.root}>
      <IconicHeader selectedTab={selectedTab} onChange={handleTabChange} />

      <div className={classes.container}>
        <div className={classes.artWrap}>
          <img src={usdxxEarnArt} alt="USDXX tokens and rewards" />
        </div>

        <div className={classes.content}>
          <div className={classes.flexContainerRoot}>
            <img src={usdxxLogo} alt="" />
            <h2>Smart USDXX</h2>
          </div>

          <Typography className={classes.eyebrow}>
            USDXX EARN · OPT-IN ONLY
          </Typography>
          <Typography className={classes.heading}>
            Yield is a choice
            <br />
            <em>never the default.</em>
          </Typography>
          <Typography className={classes.text}>
            Holding USDXX keeps you 1:1 and redeemable, with no interest
            attached to the token. USDXX Earn is a{' '}
            <strong>separate, opt-in program</strong> with its own terms,
            risk disclosures and eligibility. Earn balances are not part of
            the USDXX redemption reserve.
          </Typography>

          <Box className={classes.card}>
            <InputField
              label="Amount to allocate"
              type="text"
              placeholder="0.0"
              value={amount}
              onChange={handleAmountChange}
              inputProps={{ inputMode: 'decimal' }}
              endAdornment={
                <InputAdornment position="end">
                  <div className={classes.dropDownIconContainer}>
                    <p>USDXX</p>
                  </div>
                </InputAdornment>
              }
            />

            <Typography className={classes.label} style={{ marginTop: 20 }}>
              Commitment term
            </Typography>
            <div className={classes.termGrid}>
              {Object.entries(EARN_TERMS).map(([term, apy]) => (
                <Button
                  key={term}
                  variant="contained"
                  className={`${classes.termButton} ${
                    months === term
                      ? classes.activeButton
                      : classes.inactiveButton
                  }`}
                  onClick={() => setMonths(term)}
                >
                  <b>{term} months</b>
                  <span>{apy.toFixed(2)}% indicative APY</span>
                </Button>
              ))}
            </div>

            <div className={classes.rewardRow}>
              <div>
                <span>Estimated interest</span>
                <strong>
                  {estimatedInterest.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}{' '}
                  USDXX
                </strong>
              </div>
              <div>
                <span>Estimated total</span>
                <strong>
                  {estimatedTotal.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}{' '}
                  USDXX
                </strong>
              </div>
            </div>

            <GenericButton text="Coming Soon" disabled onClick={() => {}} />

            <Typography className={classes.disclaimer}>
              USDXX Earn is in design and will launch separately from the
              stablecoin, after independent legal, tax and risk review.
              Indicative rates only, shown below reserve yield — not a
              guaranteed return, deposit rate, or live offer.
            </Typography>
          </Box>
        </div>
      </div>
    </div>
  );
};

export default SmartUSDXX;
